import React, { useState } from 'react';
import {
	Modal,
	Upload,
	Table,
	Button,
	message,
	Alert,
	Space,
	Typography,
	Row,
	Col,
	Steps,
	Tag,
} from 'antd';
import {
	CloudUploadOutlined,
	DownloadOutlined,
	DeleteOutlined,
	InfoCircleOutlined,
	CloseOutlined,
} from '@ant-design/icons';
import { TSmartVariableDefinition } from '@/types/TSmartCRM';
import { parseCSV, validateCSVData, downloadSampleCSV } from '@/utils/SmartCRM/csvUtils';
import { EFieldType } from 'src/enums/ESmartCRM';

const { Text, Title } = Typography;

interface CSVUploadModalProps {
	visible: boolean;
	onCancel: () => void;
	onSubmit: (data: any[]) => Promise<void>;
	variables: TSmartVariableDefinition[];
}

export const CSVUploadModal: React.FC<CSVUploadModalProps> = ({
	visible,
	onCancel,
	onSubmit,
	variables,
}) => {
	const [currentStep, setCurrentStep] = useState(0);
	const [validData, setValidData] = useState<any[]>([]);
	const [errors, setErrors] = useState<string[]>([]);
	const [duplicates, setDuplicates] = useState<string[]>([]);
	const [loading, setLoading] = useState(false);
	const [showAlerts, setShowAlerts] = useState(true);

	const requiredFields = variables.filter((v) => v.isRequired);

	const resetState = () => {
		setCurrentStep(0);
		setValidData([]);
		setErrors([]);
		setDuplicates([]);
		setLoading(false);
		setShowAlerts(true);
	};

	const handleCancel = () => {
		resetState();
		onCancel();
	};

	const handleUpload = async (file: File) => {
		if (!file.name.endsWith('.csv')) {
			message.error('Please upload a CSV file');
			return false;
		}

		setLoading(true);
		try {
			const rawData = await parseCSV(file);

			if (rawData.length === 0) {
				message.error('CSV file is empty');
				setLoading(false);
				return false;
			}

			const result = validateCSVData(rawData, variables);
			setValidData(result.validRows);

			const errorMessages: string[] = [];
			const duplicateMessages: string[] = [];

			result.rows.forEach((row, index) => {
				if (row.errors.length > 0) {
					errorMessages.push(`Row ${index + 1}: ${row.errors.map((e) => e.message).join(', ')}`);
				}
				if (row.isDuplicate) {
					duplicateMessages.push(`Row ${index + 1}: Duplicate ${row.duplicateField}`);
				}
			});

			setErrors(errorMessages);
			setDuplicates(duplicateMessages);
			setCurrentStep(1);

			message.success(
				`Found ${result.validRows.length} valid records. ${errorMessages.length} errors and ${duplicateMessages.length} duplicates skipped.`,
			);
		} catch (_error) {
			message.error('Failed to process CSV file');
		} finally {
			setLoading(false);
		}
		return false;
	};

	const handleDelete = (index: number) => {
		const newData = [...validData];
		newData.splice(index, 1);
		setValidData(newData);
		message.success('Record deleted');
	};

	const handleSubmit = async () => {
		if (validData.length === 0) {
			message.error('No valid data to import');
			return;
		}

		setLoading(true);
		try {
			await onSubmit(validData);
			resetState();
			onCancel();
		} catch (_error) {
			message.error('Failed to import data');
		} finally {
			setLoading(false);
		}
	};

	const getColumns = () => {
		const baseColumns = [
			{
				title: '',
				width: 50,
				render: (_: any, __: any, index: number) => (
					<Button
						type="text"
						size="small"
						icon={<DeleteOutlined />}
						onClick={() => handleDelete(index)}
						className="text-red-500 hover:text-red-700"
					/>
				),
			},
			{ title: 'Phone', dataIndex: 'number', width: 140 },
		];

		const requiredCols = requiredFields.map((field) => ({
			title: `${field.fieldName} *`,
			dataIndex: field.internalKey,
			width: 120,
			render: (value: any) => {
				if (value === null || value === undefined || value === '') {
					return <Text type="secondary">-</Text>;
				}
				if (field.fieldType === EFieldType.BOOLEAN) {
					const isTrue = value === true || value === 'true';
					return isTrue ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>;
				}
				if (field.fieldType === EFieldType.DATE && value) {
					return new Date(value).toLocaleDateString();
				}
				return String(value);
			},
		}));

		return [...baseColumns, ...requiredCols];
	};

	const renderUploadStep = () => (
		<div className="space-y-6">
			<Alert
				message="Smart Import Features"
				description="• Automatically handles duplicates • Delete bad rows • Only imports valid data • Phone number auto-formatting"
				type="info"
				icon={<InfoCircleOutlined />}
				showIcon
				closable
			/>

			<div className="bg-gray-50 p-6 rounded-lg">
				<Title level={4} className="mb-4">
					CSV Requirements
				</Title>
				<div className="mb-4">
					<Text className="text-gray-700">
						Required columns: <Text strong>number</Text>
						{requiredFields.length > 0 && (
							<span>
								, <Text strong>{requiredFields.map((f) => f.fieldName).join(', ')}</Text>
							</span>
						)}
					</Text>
				</div>
				<Button
					icon={<DownloadOutlined />}
					onClick={() => downloadSampleCSV(variables)}
					type="link"
					className="p-0 text-blue-600"
				>
					Download Sample CSV Template
				</Button>
			</div>

			<div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center bg-gray-50">
				<Upload.Dragger
					customRequest={({ file }) => handleUpload(file as File)}
					showUploadList={false}
					accept=".csv"
					className="border-none bg-transparent"
				>
					<div className="flex flex-col items-center space-y-4">
						<CloudUploadOutlined className="text-6xl text-blue-500" />
						<div>
							<Text className="text-xl font-medium text-gray-700">
								Click or drag CSV file to upload
							</Text>
							<br />
							<Text className="text-gray-500">Only CSV files are supported</Text>
						</div>
					</div>
				</Upload.Dragger>
			</div>
		</div>
	);

	const renderReviewStep = () => (
		<div className="space-y-4">
			{showAlerts && (errors.length > 0 || duplicates.length > 0) && (
				<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
					<div className="flex justify-between items-start">
						<div className="flex-1 space-y-2">
							{errors.length > 0 && (
								<div className="text-sm">
									<Text type="danger" className="font-medium">
										{errors.length} Errors (Skipped):
									</Text>
									<div className="text-xs text-gray-600 mt-1 max-h-16 overflow-y-auto">
										{errors.slice(0, 3).join('; ')}
										{errors.length > 3 && ` +${errors.length - 3} more`}
									</div>
								</div>
							)}
							{duplicates.length > 0 && (
								<div className="text-sm">
									<Text type="warning" className="font-medium">
										{duplicates.length} Duplicates (Skipped):
									</Text>
									<div className="text-xs text-gray-600 mt-1 max-h-16 overflow-y-auto">
										{duplicates.slice(0, 3).join('; ')}
										{duplicates.length > 3 && ` +${duplicates.length - 3} more`}
									</div>
								</div>
							)}
						</div>
						<Button
							type="text"
							size="small"
							icon={<CloseOutlined />}
							onClick={() => setShowAlerts(false)}
							className="text-gray-400 hover:text-gray-600"
						/>
					</div>
				</div>
			)}

			<Row gutter={16} className="text-center">
				<Col span={8}>
					<div className="bg-green-50 p-3 rounded-lg">
						<div className="text-2xl font-bold text-green-600">{validData.length}</div>
						<div className="text-sm text-green-700">Valid Records</div>
					</div>
				</Col>
				<Col span={8}>
					<div className="bg-red-50 p-3 rounded-lg">
						<div className="text-2xl font-bold text-red-600">{errors.length}</div>
						<div className="text-sm text-red-700">Errors</div>
					</div>
				</Col>
				<Col span={8}>
					<div className="bg-yellow-50 p-3 rounded-lg">
						<div className="text-2xl font-bold text-yellow-600">{duplicates.length}</div>
						<div className="text-sm text-yellow-700">Duplicates</div>
					</div>
				</Col>
			</Row>

			{validData.length > 0 && (
				<Table
					dataSource={validData}
					columns={getColumns()}
					pagination={{ pageSize: 8, showSizeChanger: false }}
					scroll={{ x: 800, y: 300 }}
					size="small"
					rowKey={(_, index) => index || 0}
					className="border rounded-lg"
				/>
			)}
		</div>
	);

	return (
		<Modal
			title="Import Customers from CSV"
			open={visible}
			onCancel={handleCancel}
			width={1200}
			footer={
				<div className="flex justify-between">
					<div>
						{currentStep === 1 && <Button onClick={() => setCurrentStep(0)}>Back to Upload</Button>}
					</div>
					<Space>
						<Button onClick={handleCancel}>Cancel</Button>
						{currentStep === 1 && validData.length > 0 && (
							<Button type="primary" onClick={handleSubmit} loading={loading}>
								Import {validData.length} Records
							</Button>
						)}
					</Space>
				</div>
			}
		>
			<div className="mb-6">
				<Steps current={currentStep}>
					<Steps.Step title="Upload & Validate" icon={<CloudUploadOutlined />} />
					<Steps.Step title="Review & Import" />
				</Steps>
			</div>

			{currentStep === 0 ? renderUploadStep() : renderReviewStep()}
		</Modal>
	);
};
